import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentResult({ cancel }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState(cancel ? "cancelled" : "checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (cancel) return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("payment status poll retry", e);
      }
      attempts.current += 1;
      if (attempts.current > 15) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancel, params, refresh]);

  const M = {
    checking: { icon: <Loader2 className="h-10 w-10 animate-spin text-[var(--brand)]" />, t: "Confirming your payment…", s: "Hang tight, this only takes a moment." },
    paid: { icon: <CheckCircle2 className="h-10 w-10 text-green-600" />, t: "Payment successful", s: "Your purchase has been applied to your account." },
    cancelled: { icon: <XCircle className="h-10 w-10 text-[var(--ink-soft)]" />, t: "Payment cancelled", s: "No charge was made." },
    timeout: { icon: <Loader2 className="h-10 w-10 text-[var(--brand)]" />, t: "Still processing", s: "It's taking longer than usual — check your Team page shortly." },
    error: { icon: <XCircle className="h-10 w-10 text-[var(--brand)]" />, t: "Something went wrong", s: "We couldn't confirm this payment." },
  }[state];

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bone)] px-6">
      <div className="w-full max-w-md rounded-2xl border border-[var(--line)] bg-white p-8 text-center shadow-sm">
        <div className="mb-4 flex justify-center">{M.icon}</div>
        <h1 className="font-display text-2xl tracking-tight">{M.t}</h1>
        <p className="mt-2 text-sm text-[var(--ink-soft)]">{M.s}</p>
        <button data-testid="payment-back-btn" onClick={() => navigate("/team")}
          className="mt-6 w-full rounded-full bg-[var(--ink)] py-3 text-sm font-semibold text-white transition-colors hover:opacity-90">
          Back to Team
        </button>
      </div>
    </div>
  );
}
